// 005 (FR-040; contracts: server 005 catalogue, 006 booklists, 008 cart): what the shopping screens
// call, as one injected client. The parent implementation is here; the school admin's on-behalf
// implementation (010) lives in the school-admin chunk and is provided through ShopClientContext,
// so parent screens never load it (FR-041; 006 FR-037).
import type { Href } from "expo-router";
import { createContext, useContext } from "react";

import { api } from "@/api";
import { type ApiClient, unwrap } from "@/api/client";
import type { Api } from "@/api/types";

import type { CartStore } from "./cartStore";

export type CategoryCode = Api["CategoryCode"];
export type ProductQuery = {
  category: CategoryCode;
  subject?: Api["Subject"];
  subjectGroup?: "LANGUAGES";
  kind?: Api["ProductKind"];
  page: number;
};

export type ShopClient = {
  listCategories(): Promise<Api["CategoryList"]>;
  listProducts(
    studentId: string,
    query: ProductQuery,
  ): Promise<Api["StudentProductPage"]>;
  getBooklist(studentId: string): Promise<Api["StudentBooklist"]>;
  getCart(): Promise<Api["Cart"]>;
  setLine(body: Api["CartLineSet"]): Promise<Api["Cart"]>;
  addFullSet(studentId: string): Promise<Api["CartFullSetResult"]>;
  // 009: checkout and payment (server 008).
  createCheckout(
    body: Api["CheckoutCreate"],
    idempotencyKey: string,
  ): Promise<Api["Checkout"]>;
  getCheckout(checkoutId: string): Promise<Api["Checkout"]>;
  /** Local/staging fake gateway only. */
  simulatePayment(
    checkoutId: string,
    outcome: "SUCCESS" | "FAILURE",
  ): Promise<Api["Checkout"]>;
  reportPayment(
    checkoutId: string,
    result: Api["PaymentResult"],
  ): Promise<Api["Checkout"]>;
};

export function createParentShopClient(client: ApiClient): ShopClient {
  return {
    listCategories: () => unwrap(client.GET("/api/v1/categories")),
    listProducts: (studentId, q) =>
      unwrap(
        client.GET("/api/v1/products", {
          params: {
            query: {
              studentId,
              category: q.category,
              page: q.page,
              size: 25,
              ...(q.subject ? { subject: q.subject } : {}),
              ...(q.subjectGroup ? { subjectGroup: q.subjectGroup } : {}),
              ...(q.kind ? { kind: q.kind } : {}),
            },
          },
        }),
      ),
    getBooklist: (studentId) =>
      unwrap(
        client.GET("/api/v1/students/{studentId}/booklist", {
          params: { path: { studentId } },
        }),
      ),
    getCart: () => unwrap(client.GET("/api/v1/cart")),
    setLine: (body) => unwrap(client.PUT("/api/v1/cart/lines", { body })),
    addFullSet: (studentId) =>
      unwrap(client.POST("/api/v1/cart/full-set", { body: { studentId } })),
    createCheckout: (body, key) =>
      unwrap(
        client.POST("/api/v1/checkouts", {
          params: { header: { "Idempotency-Key": key } },
          body,
        }),
      ),
    getCheckout: (checkoutId) =>
      unwrap(
        client.GET("/api/v1/checkouts/{checkoutId}", {
          params: { path: { checkoutId } },
        }),
      ),
    simulatePayment: (checkoutId, outcome) =>
      unwrap(
        client.POST("/api/v1/checkouts/{checkoutId}/fake-payment", {
          params: { path: { checkoutId } },
          body: { outcome },
        }),
      ),
    reportPayment: (checkoutId, result) =>
      unwrap(
        client.POST("/api/v1/checkouts/{checkoutId}/payment-result", {
          params: { path: { checkoutId } },
          body: result,
        }),
      ),
  };
}

/** The shopping context (FR-040): which client to call and how the screens label the shopper. */
export type ShopperStudent = Pick<
  Api["Student"],
  "id" | "name" | "classLevel" | "section"
> & { school?: Api["Student"]["school"] };

export type ShoppingContext = {
  mode: "parent" | "onBehalf";
  client: ShopClient;
  /** On behalf: the one student and the banner text ("Ordering on behalf of …"). */
  onBehalf?: { student: ShopperStudent; banner: string };
  /** The cart store to use; parents use the app's one (010: one store per student). */
  store?: CartStore;
  /** 010: View cart and Browse School Books stay inside the booking screen. */
  onViewCart?: () => void;
  onBrowseBooks?: () => void;
  /** Where View cart goes. */
  cartHref: Href;
};

export const ShoppingContextValue = createContext<ShoppingContext>({
  mode: "parent",
  client: createParentShopClient(api),
  cartHref: "/cart",
});
export const useShopping = () => useContext(ShoppingContextValue);
